import React from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add,
  Code as CodeIcon,
  FormatBold as FormatBoldIcon,
  FormatItalic as FormatItalicIcon,
  FormatListBulleted as FormatListBulletedIcon,
  FormatListNumbered as FormatListNumberedIcon,
  HelpOutline,
  InsertEmoticon as InsertEmoticonIcon,
  StrikethroughS as StrikethroughSIcon,
} from "@mui/icons-material";

/**
 * BodySection — sección del cuerpo del template
 * Props:
 * - category
 * - body, setBody
 * - bodyVarIndices, setBodyVarIndices
 * - bodySamples, setBodySamples
 * - BODY_LIMIT
 * - MAX_BODY_VARS
 * - wrapSelection, applyList
 * - bodyInputRef
 * - emojiAnchor, setEmojiAnchor, setEmojiPickerRef
 * - handleAddBodyVariable
 */
export default function BodySection({
  category,
  body,
  setBody,
  bodyError,
  bodyVarIndices,
  setBodyVarIndices,
  bodySamples,
  setBodySamples,
  BODY_LIMIT,
  MAX_BODY_VARS,
  wrapSelection,
  applyList,
  bodyInputRef,
  emojiAnchor,
  setEmojiAnchor,
  setEmojiPickerRef,
  handleAddBodyVariable,
}) {
  // 🚫 Oculta si es autenticación
  if (category === "authentication") return null;

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
      <Stack gap={1.5}>
        {/* Header + toolbar */}
        <Stack
          direction="row"
          alignItems="center"
          gap={1}
          sx={{ flexWrap: "wrap" }}
        >
          <Typography variant="h6">Cuerpo</Typography>

          <Tooltip title="El cuerpo admite variables {{n}} (máx 10) y formatos: *bold*, _italic_, ~strike~, `mono`.">
            <HelpOutline fontSize="small" />
          </Tooltip>

          <Chip size="small" label={`Variables: ${bodyVarIndices.length}/10`} />

          <Box sx={{ flex: 1 }} />

          {/* Toolbar */}
          <Stack direction="row" gap={0.5}>
            <IconButton
              size="small"
              onClick={(e) => setEmojiAnchor(e.currentTarget)}
            >
              <InsertEmoticonIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => wrapSelection("*")}>
              <FormatBoldIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => wrapSelection("_")}>
              <FormatItalicIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => wrapSelection("~")}>
              <StrikethroughSIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => wrapSelection("`")}>
              <CodeIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => applyList(true)}>
              <FormatListNumberedIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => applyList(false)}>
              <FormatListBulletedIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>

        {/* Emoji popover */}
        <Popover
          open={Boolean(emojiAnchor)}
          anchorEl={emojiAnchor}
          onClose={() => setEmojiAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <Box sx={{ p: 1, "& emoji-picker": { width: 320, height: 380 } }}>
            <emoji-picker
              ref={setEmojiPickerRef}
              emoji-style="native"
            ></emoji-picker>
          </Box>
        </Popover>

        {/* Campo principal */}
        <TextField
          multiline
          minRows={6}
          required
          size="small"
          placeholder="Escribe el texto de tu mensaje en el idioma seleccionado"
          value={body}
          onChange={(e) => setBody(e.target.value.slice(0, BODY_LIMIT))}
          inputRef={bodyInputRef}
          error={bodyError}
          helperText={
            bodyError && body.trim().length === 0
              ? "El cuerpo del mensaje es requerido"
              : body.length > BODY_LIMIT
                ? `Excede el límite de ${BODY_LIMIT} caracteres`
                : `${body.length}/${BODY_LIMIT}`
          }
          fullWidth
        />

        {/* Botón para agregar variables */}
        <Stack direction="row" gap={1} alignItems="center">
          <Button
            variant="outlined"
            size="small"
            startIcon={<Add />}
            onClick={handleAddBodyVariable}
            disabled={bodyVarIndices.length >= MAX_BODY_VARS}
          >
            Agregar variable
          </Button>

          {bodyVarIndices.length >= MAX_BODY_VARS && (
            <Typography variant="caption" color="error">
              Máximo de {MAX_BODY_VARS} variables alcanzado
            </Typography>
          )}
        </Stack>

        {/* Variables */}
        {bodyVarIndices.length > 0 && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Define valor de ejemplo para variable del cuerpo
            </Typography>

            <Stack gap={1}>
              {bodyVarIndices.map((id) => (
                <Stack key={id} direction="row" alignItems="center" gap={1}>
                  <Chip label={`{{${id}}}`} size="small" />
                  <TextField
                    fullWidth
                    placeholder="Valor de ejemplo para la variable"
                    size="small"
                    value={bodySamples[id] || ""}
                    onChange={(e) =>
                      setBodySamples((prev) => ({
                        ...prev,
                        [id]: e.target.value,
                      }))
                    }
                  />
                </Stack>
              ))}
            </Stack>
          </Box>
        )}
      </Stack>
    </Paper>
  );
}
